<template>
    <div>
        <b-row>
          <b-col>
            <h2>{{ $t('participant') }}</h2>
            <p>{{ participantNames }}</p>
            <h2>{{ $t('test') }}</h2>
            <p>{{ testMethodEdited }}</p>
          </b-col>
          <b-col style="text-align: right;">
            <h2>{{ $t('report_cost') }}</h2>
            <p>{{ pdfRequirements.testIsPaid ? 0 : pdfRequirements.creditCost }} {{ $t('privatePage.credits') }}</p>
            <h2>{{ $t('available_credits') }}</h2>
            <p>{{ userCompany.credits }} {{ $t('privatePage.credits') }}</p>
          </b-col>
        </b-row>
        <hr class="my-4">
        <b-row class="mb-3">
          <b-col>
            <h2>{{ $t('norm_group') }}</h2>
            <b-form-select v-model="teamReportValues.selectedNormGroup" :options="optionsNormGroups" value-field="id" text-field="name" />
          </b-col>
        </b-row>
        <b-row class="mb-3">
          <b-col>
            <h2>{{ $t('lang') }}</h2>
            <b-form-select v-model="teamReportValues.selectedLanguage" :options="optionsLanguages" value-field="languageId" text-field="langName" />
          </b-col>
        </b-row>
        <b-row v-if="userCompany.hasTestInterpreterPermissions" class="mb-3">
          <b-col>
            <p>{{this.teamProfileMessage}}</p>
          </b-col>
          <b-col>
            <b-button class="pull-right" v-bind:disabled="!isValidSelectionForStandardReport" variant="primary" @click="generateTeamReport" style="margin-right: 5px;">{{ $t('create_teamprofile') }}</b-button>
          </b-col>
        </b-row>
        <hr class="my-4">
        <b-row v-if="hasExpertReports">
          <b-col>
            <h2>{{ $t('expert_report') }}</h2>
            <b-form-select v-model="teamReportValues.selectedExpertReportId" :options="optionsExpertReports" value-field="id" text-field="description"/>
            <p>{{this.expertReportMessage}}</p>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <h2>{{ $t('team_name') }}</h2>
            <b-form-input type="text" v-model="teamReportValues.teamName" />
          </b-col>
          <b-col class="team-report-actions">
            <b-button class="pull-right" :disabled="disableTeamReportButton" variant="primary" @click="generateFrontendPdf">{{ $t('create_teamreport') }} <i v-if="savingPdf" class="fa fa-spinner fa-pulse"></i></b-button>
          </b-col>
        </b-row>

        <b-row class="team-report-content" v-if="reportData">
          <b-col>
            <div ref="team-report-front-page">
              <team-report-front-page :normGroup="selectedNormGroupName" :titleKey="selectedReportKey" :teamName="teamReportValues.teamName" :lang="teamReportValues.selectedLanguage" />
            </div>
            <div ref="team-report-intro-page">
              <team-report-intro-page :lang="teamReportValues.selectedLanguage" />
            </div>
            <div ref="team-report-description-page">
              <team-report-description-page :lang="teamReportValues.selectedLanguage" />
            </div>
            <div ref="team-report-result-team1-page">
              <team-report-result-team1-page :reportData="reportData" :lang="teamReportValues.selectedLanguage" />
            </div>
            <div ref="team-report-result-team2-page">
              <team-report-result-team2-page :reportData="reportData" :lang="teamReportValues.selectedLanguage" />
            </div>

            <template v-if="reportData.totalCount <= 5">
              <div ref="team-report-result-ind1-page" class="printable-page">
                <team-report-result-individual-intro :lang="teamReportValues.selectedLanguage" />
                <i18n-t keypath="pdfReport.teampage1_leadership_spread" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="true" :data="reportData.individuals" :isSpread="true" :lang="teamReportValues.selectedLanguage" />
                <i18n-t keypath="pdfReport.teampage1_leadership_characteristic" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="true" :data="reportData.individuals" :isSpread="false" :lang="teamReportValues.selectedLanguage" />
                <i18n-t keypath="pdfReport.teampage2_manager_spread" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="false" :data="reportData.individuals" :isSpread="true" :lang="teamReportValues.selectedLanguage" />
                <i18n-t keypath="pdfReport.teampage2_manager_characteristic" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="false" :data="reportData.individuals" :isSpread="false" :lang="teamReportValues.selectedLanguage" />
              </div>
            </template>
            <template v-else-if="reportData.totalCount <= 10">
              <div ref="team-report-result-ind1-page" class="printable-page">
                <team-report-result-individual-intro :lang="teamReportValues.selectedLanguage" />
                <i18n-t keypath="pdfReport.teampage1_leadership_spread" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="true" :data="reportData.individuals" :isSpread="true" :lang="teamReportValues.selectedLanguage" />
                <i18n-t keypath="pdfReport.teampage1_leadership_characteristic" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="true" :data="reportData.individuals" :isSpread="false" :lang="teamReportValues.selectedLanguage" />
              </div>
              <div ref="team-report-result-ind2-page" class="printable-page">
                <i18n-t keypath="pdfReport.individualpage2_heading" :locale="teamReportValues.selectedLanguage" tag="h2"></i18n-t>
                <i18n-t keypath="pdfReport.teampage2_manager_spread" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="false" :data="reportData.individuals" :isSpread="true" :lang="teamReportValues.selectedLanguage" />
                <i18n-t keypath="pdfReport.teampage2_manager_characteristic" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="false" :data="reportData.individuals" :isSpread="false" :lang="teamReportValues.selectedLanguage" />
              </div>
            </template>
            <template v-else>
              <div ref="team-report-result-ind1-page" class="printable-page">
                <team-report-result-individual-intro :lang="teamReportValues.selectedLanguage" />
                <i18n-t keypath="pdfReport.teampage1_leadership_spread" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="true" :data="reportData.individuals" :isSpread="true" :lang="teamReportValues.selectedLanguage" />
              </div>
              <div ref="team-report-result-ind2-page" class="printable-page">
              <i18n-t keypath="pdfReport.individualpage2_heading" :locale="teamReportValues.selectedLanguage" tag="h2"></i18n-t>
                <i18n-t keypath="pdfReport.teampage1_leadership_characteristic" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="true" :data="reportData.individuals" :isSpread="false" :lang="teamReportValues.selectedLanguage" />
              </div>
              <div ref="team-report-result-ind3-page" class="printable-page">
                <i18n-t keypath="pdfReport.individualpage2_heading" :locale="teamReportValues.selectedLanguage" tag="h2"></i18n-t>
                <i18n-t keypath="pdfReport.teampage2_manager_spread" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="false" :data="reportData.individuals" :isSpread="true" :lang="teamReportValues.selectedLanguage" />
              </div>
              <div ref="team-report-result-ind4-page" class="printable-page">
                <i18n-t keypath="pdfReport.individualpage2_heading" :locale="teamReportValues.selectedLanguage" tag="h2"></i18n-t>
                <i18n-t keypath="pdfReport.teampage2_manager_characteristic" :locale="teamReportValues.selectedLanguage" tag="h3"></i18n-t>
                <individual-results :multiColor="false" :data="reportData.individuals" :isSpread="false" :lang="teamReportValues.selectedLanguage" />
              </div>
            </template>
          </b-col>
        </b-row>
    </div>
</template>

<script>
import jsPDF from 'jspdf'
import { mapActions, mapGetters } from 'vuex'
import TeamReportFrontPage from '../Reports/TeamReportFrontPage'
import TeamReportIntroPage from '../Reports/TeamReportIntroPage'
import TeamReportDescriptionPage from '../Reports/TeamReportDescriptionPage'
import TeamReportResultTeam1Page from '../Reports/TeamReportResultTeam1Page'
import TeamReportResultTeam2Page from '../Reports/TeamReportResultTeam2Page'
import TeamReportResultIndividualIntro from '../Reports/TeamReportResultIndividualIntro'
import IndividualResults from '../IndividualResults/IndividualResults'

export default {
  components: {
    TeamReportFrontPage,
    TeamReportIntroPage,
    TeamReportDescriptionPage,
    TeamReportResultTeam1Page,
    TeamReportResultTeam2Page,
    TeamReportResultIndividualIntro,
    IndividualResults
  },
  computed: {
    ...mapGetters(['userId']),
    participantNames() {
      const nrOfNamesShown = 6
      let allNames = []
      let rows = this.selectedRows || []
      rows.forEach((item, index) => {
        if (index > (nrOfNamesShown - 1)) return
        allNames.push(`${item.firstName} ${item.lastName}`)
      })
      return rows.length > nrOfNamesShown ? `${allNames.join(', ')} ...` : allNames.join(', ')
    },
    disableTeamReportButton() {
      return this.savingPdf || !this.teamReportValues.teamName || !this.teamReportValues.selectedLanguage || !this.teamReportValues.selectedNormGroup
    },
    testMethodEdited() {
      const rows = this.selectedRows
      if (!rows || rows.length === 0) return ''
      return rows[0].testMethodName
    },
    isValidSelectionForStandardReport() {
      return !!this.teamReportValues.selectedNormGroup && !!this.teamReportValues.selectedLanguage
    },
    isValidSelectionForExpertReport() {
      let isValid = false
      if (
        this.isValidSelectionForStandardReport &&
        this.hasExpertReports
        // For now we only have english, so this check is not necessary yet
        /* &&
        this.selectedExpertReport &&
        this.selectedExpertReport.languages &&
        this.selectedExpertReport.languages.constructor === Array &&
        this.selectedExpertReport.languages.filter(
          lang => lang === this.teamReportValues.selectedLanguage
        ).length === 1 */
      ) {
        isValid = true
      }
      return isValid
    },
    expertReportExtraCreditCost() {
      if (this.isValidSelectionForExpertReport) {
        return this.selectedExpertReport.extraCreditCost
      }
    },
    selectedNormGroupName() {
      const normGroup = (this.optionsNormGroups || '').find(n => n.id === this.teamReportValues.selectedNormGroup)
      return (normGroup && normGroup.name) || ''
    },
    selectedReportKey() {
      const report = this.optionsExpertReports.find(item => item.id === this.teamReportValues.selectedExpertReportId)
      return (report && report.name) || ''
    },
    teamProfileMessage() {
      if (this.pdfRequirements && this.pdfRequirements.expertReportOptions) {
        if (this.teamReportValues.selectedNormGroup && this.teamReportValues.selectedLanguage) {
          const teamProfileExtras = this.pdfRequirements.expertReportOptions.find(repOpt => repOpt.name === 'TeamProfile')
          const credits = teamProfileExtras && teamProfileExtras.extraCreditCost
          return this.$t('privatePage.expert_report_cost', { credits: credits })
        }
      }
      return ''
    },
    expertReportMessage() {
      if (this.selectedExpertReport && this.userCompany.hasTestInterpreterPermissions) {
        if (this.isValidSelectionForExpertReport) {
          if (!this.selectedExpertReport.isPaid) {
            return this.$t('privatePage.expert_report_cost', { credits: this.expertReportExtraCreditCost })
          }
        } else if (this.selectedNormGroup === null) {
          return this.$t('privatePage.expert_report_please_select', { selection: this.$t('norm_group') })
        } else if (this.selectedLanguage === null) {
          return this.$t('privatePage.expert_report_please_select', { selection: this.$t('lang') })
        } else {
          return this.$t('privatePage.expert_report_invalid_selection_team')
        }
      }
    },
    selectedExpertReport() {
      if (this.hasExpertReports) {
        return this.pdfRequirements.expertReportOptions.filter(repOpt => repOpt.id === this.teamReportValues.selectedExpertReportId)[0]
      }
    }
  },
  data() {
    return {
      savingPdf: false,
      reportData: undefined
    }
  },
  methods: {
    ...mapActions({
      showErrorMessage: 'showErrorMessage'
    }),
    ...mapActions('testSuitesModule', {
      getPdfRequirementsTeamProfile: 'getPdfRequirementsTeamProfile',
      getTeamReportData: 'getTeamReportData'
    }),
    generateFrontendPdf: async function() {
      this.savingPdf = true
      await this.fetchAndSetupReportData()
      await this.saveFrontendPdf()
      this.savingPdf = false
    },
    fetchAndSetupReportData: async function() {
      const testIds = this.selectedRows.map(i => i.testId)
      const payload = {
        languageId: this.teamReportValues.selectedLanguage,
        normGroupId: this.teamReportValues.selectedNormGroup,
        tests: testIds,
        userId: this.userId,
        reportId: this.teamReportValues.selectedExpertReportId
      }
      const fetchedData = await this.getTeamReportData(payload)
        .catch(() => {
          this.savingPdf = false
          this.showErrorMessage(this.$t('pdfReport.error_failed_to_create'))
        })
      this.reportData = fetchedData || undefined
    },
    saveFrontendPdf: async function() {
      /* eslint-disable new-cap */
      const doc = new jsPDF('p', 'pt', 'a4', true) // default doc size [595.28, 841.89] in pt:s
      const frontPage = this.$refs['team-report-front-page']
      const introPage = this.$refs['team-report-intro-page']
      const descriptionPage = this.$refs['team-report-description-page']
      const resultTeam1Page = this.$refs['team-report-result-team1-page']
      const resultTeam2Page = this.$refs['team-report-result-team2-page']
      const resultInd1Page = this.$refs['team-report-result-ind1-page']
      const resultInd2Page = this.$refs['team-report-result-ind2-page']
      const resultInd3Page = this.$refs['team-report-result-ind3-page']
      const resultInd4Page = this.$refs['team-report-result-ind4-page']

      const addFooter = () => {
        const date = new Date()
        const currentYear = date.getFullYear()
        doc.setFontSize(7)
        doc.text(this.$t('pdfReport.copyright', { year: currentYear }), 30, 810)
      }

      await doc.html(frontPage)
      addFooter()

      doc.addPage()
      await doc.html(introPage, { x: 0, y: 885 })
      addFooter()

      doc.addPage()
      await doc.html(descriptionPage, { x: 0, y: 1725 })
      addFooter()

      doc.addPage()
      await doc.html(resultTeam1Page, { x: 0, y: 2565 })
      addFooter()

      doc.addPage()
      await doc.html(resultTeam2Page, { x: 0, y: 3405 })
      addFooter()

      doc.addPage()
      await doc.html(resultInd1Page, { x: 0, y: 4245 })
      addFooter()

      if (resultInd2Page) {
        doc.addPage()
        await doc.html(resultInd2Page, { x: 0, y: 5085 })
        addFooter()
      }
      if (resultInd3Page) {
        doc.addPage()
        await doc.html(resultInd3Page, { x: 0, y: 5925 })
        addFooter()
      }
      if (resultInd4Page) {
        doc.addPage()
        await doc.html(resultInd4Page, { x: 0, y: 6765 })
        addFooter()
      }

      const filename = `Team report ${new Date().toLocaleDateString('sv-SE')}.pdf`
      await doc.save(filename)
    }
  },
  props: {
    generateExpertTeamReport: Function,
    generateTeamReport: Function,
    hasExpertReports: Boolean,
    optionsExpertReports: Array,
    optionsLanguages: Array,
    optionsNormGroups: Array,
    pdfRequirements: Object,
    selectedRows: Array,
    teamReportValues: Object,
    userCompany: Object
  }
}
</script>

<style scoped>
.fa-spinner {
  margin-left: 5px;
  color: #ffffff;
}
.team-report-actions {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
}
.team-report-content {
  opacity: 0;
  height: 0;
}
.printable-page {
  padding: 0 60px;
}
.printable-page h2 {
  font-size: 20px!important;
  margin: 0 0 4px 0!important;
  padding: 0!important;
  color: #000000;
}
.printable-page h3 {
  font-size: 14px!important;
  margin: 16px 0 8px 0!important;
  padding: 0!important;
  color: #000000;
}
</style>
